import React, { useLayoutEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { useSelector } from 'react-redux'

import styled from 'styled-components/native'

import { ProviderDashboardCard } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/providerDashboardCard/ProviderDashboardCard'

import { useClientActivity } from './hooks/useClientActivity'
import { useProviderTasks } from './hooks/useProviderTasks'
import { useRiskAlerts } from './hooks/useRiskAlerts'
import ClientActivityPanel from './panels/ClientActivityPanel'
import RiskAlertsPanel from './panels/RiskAlertsPanel'
import TasksPanel from './panels/TasksPanel'
import { actions as mixpanelActions, mixpanelEvents } from '../../../../mixpanel/mixpanelConstants'
import { track } from '../../../../mixpanel/mixpanelTracking'
import { getActiveOpenItemsTab } from '../../data/appSelectors'
import { setActiveOpenItemsTab } from '../../data/appSlice'
import { useAppDispatch } from '../../data/store'

const ProviderCard = styled(ProviderDashboardCard)({
  flex: 1,
  maxHeight: '100%',
})

const OpenItems: React.FC = () => {
  const intl = useIntl()
  const dispatch = useAppDispatch()
  const activeTab = useSelector(getActiveOpenItemsTab)
  const displayedActiveTab = activeTab ?? 'riskAlerts'
  const [activeItemCount, setActiveItemCount] = useState<number | null>(null)

  const {
    riskAlerts,
    isLoading: isRiskAlertsLoading,
    isFetching: isRiskAlertsFetching,
    isSuccess: isRiskAlertsSuccess,
  } = useRiskAlerts()
  const { tasks, isLoading: isTasksLoading } = useProviderTasks()
  const { items: clientActivity, isLoading: isClientActivityLoading } = useClientActivity()

  const getTabCount = (tabId: string, itemCount: number) =>
    tabId === displayedActiveTab && activeItemCount !== null ? activeItemCount : itemCount

  useLayoutEffect(() => {
    if (isRiskAlertsFetching || !isRiskAlertsSuccess || activeTab !== undefined) return

    const initialTab = riskAlerts.length === 0 ? 'tasks' : 'riskAlerts'
    if (initialTab !== displayedActiveTab) {
      setActiveItemCount(null)
    }
    dispatch(setActiveOpenItemsTab(initialTab))
  }, [activeTab, dispatch, displayedActiveTab, isRiskAlertsFetching, isRiskAlertsSuccess, riskAlerts.length])

  return (
    <ProviderCard
      title='Open items'
      activeTabId={displayedActiveTab}
      onTabChange={(id: string) => {
        if (id !== displayedActiveTab) {
          setActiveItemCount(null)
        }
        dispatch(setActiveOpenItemsTab(id))
        const panel = id === 'riskAlerts' ? 'risk_alerts' : id === 'activity' ? 'client_activity' : 'tasks'
        track({
          event: mixpanelEvents.BUTTON_PRESS,
          action: mixpanelActions.OPEN_ITEMS_TAB_SELECT,
          properties: { panel },
        })
      }}
      tabs={[
        {
          id: 'riskAlerts',
          label: intl.formatMessage(
            {
              defaultMessage: 'Risk Alerts{count, plural, =0 {} other { {count}}}',
              description: 'Tab label',
            },
            { count: getTabCount('riskAlerts', riskAlerts.length) },
          ),
          content: (
            <RiskAlertsPanel
              riskAlerts={riskAlerts}
              isLoading={isRiskAlertsLoading}
              onItemCountChange={setActiveItemCount}
            />
          ),
        },
        {
          id: 'tasks',
          label: intl.formatMessage(
            { defaultMessage: 'Tasks{count, plural, =0 {} other { {count}}}', description: 'Tab label' },
            { count: getTabCount('tasks', tasks.length) },
          ),
          content: <TasksPanel tasks={tasks} isLoading={isTasksLoading} onItemCountChange={setActiveItemCount} />,
        },
        {
          id: 'activity',
          label: intl.formatMessage(
            {
              defaultMessage: 'Client Activity{count, plural, =0 {} other { {count}}}',
              description: 'Tab label',
            },
            { count: getTabCount('activity', clientActivity.length) },
          ),
          content: (
            <ClientActivityPanel
              items={clientActivity}
              isLoading={isClientActivityLoading}
              onItemCountChange={setActiveItemCount}
            />
          ),
        },
      ]}
    />
  )
}

export default OpenItems
